import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Download,
  Search,
  RefreshCw,
  Lock,
  Loader2,
  Inbox,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "hewto_admin_token";
const PAGE_SIZE = 10;

const formatDetail = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ") || fallback;
  return fallback;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Reset to page 1 whenever the search query changes.
  useEffect(() => {
    setPage(1);
  }, [query]);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setLeads([]);
  }, []);

  const loadLeads = useCallback(async (tkn) => {
    setLoadingLeads(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoadingLeads(false);
    }
  }, [logout]);

  // Validate existing token on mount
  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) {
        setChecking(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/me`, { headers: authHeader });
        if (!active) return;
        setAuthed(true);
        loadLeads(token);
      } catch {
        if (active) logout();
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      setAuthed(true);
      toast.success("Welcome back!");
      loadLeads(data.access_token);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail, "Login failed. Please try again."));
    } finally {
      setLoggingIn(false);
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter((l) =>
      [l.name, l.email, l.company, l.role, l.message, (l.document_types || []).join(" ")]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [leads, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  // Clamp current page whenever the filtered list shrinks (e.g. after refresh or search).
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const rangeStart = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filtered.length);

  const confirmDelete = async () => {
    if (!leadToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/leads/${leadToDelete.id}`, { headers: authHeader });
      setLeads((prev) => prev.filter((l) => l.id !== leadToDelete.id));
      toast.success("Lead deleted");
      setLeadToDelete(null);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Failed to delete lead."));
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = () => {
    const headers = ["Date", "Name", "Email", "Company", "Role", "Document Volume", "Document Types", "Message"];
    const rows = filtered.map((l) => [
      fmtDate(l.created_at), l.name, l.email, l.company, l.role || "",
      l.document_volume || "", (l.document_types || []).join("; "), (l.message || "").replace(/\n/g, " "),
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hewto-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- Loading splash ----------
  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  // ---------- Login ----------
  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
        <div className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl sm:p-10">
          <img src="/logo.png" alt="Hewto.ai" className="h-8 w-auto" />
          <div className="mt-6 flex items-center gap-2 text-brand">
            <Lock className="h-5 w-5" />
            <h1 className="text-2xl font-bold text-slate-900">Admin Login</h1>
          </div>
          <p className="mt-2 text-sm text-slate-500">Sign in to view Contact Sales leads.</p>
          <form onSubmit={handleLogin} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@hewto.ai"
                className="focus-visible:ring-2 focus-visible:ring-brand"
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="focus-visible:ring-2 focus-visible:ring-brand"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loggingIn}
              className="w-full rounded-full bg-brand py-6 text-base font-semibold text-white hover:bg-brand-600"
            >
              {loggingIn ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <img src="/logo.png" alt="Hewto.ai" className="h-8 w-auto" />
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-slate-500 sm:inline">Contact Sales leads</span>
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-logout-btn"
              onClick={logout}
              className="rounded-full"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Leads</h1>
            <p className="mt-1 text-slate-500">
              {leads.length} total {leads.length === 1 ? "submission" : "submissions"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="admin-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search leads..."
                className="w-56 pl-9 focus-visible:ring-2 focus-visible:ring-brand"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              data-testid="admin-refresh-btn"
              onClick={() => loadLeads(token)}
              className="rounded-full"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loadingLeads ? "animate-spin" : ""}`} />
            </Button>
            <Button
              data-testid="admin-export-btn"
              onClick={exportCsv}
              disabled={!filtered.length}
              className="rounded-full bg-brand font-semibold text-white hover:bg-brand-600"
            >
              <Download className="mr-1.5 h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loadingLeads ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Inbox className="h-10 w-10 text-slate-300" />
              <p className="mt-4 font-medium text-slate-600">
                {leads.length === 0 ? "No leads yet" : "No leads match your search"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <th className="whitespace-nowrap px-3 py-3 font-semibold">Date</th>
                    <th className="px-3 py-3 font-semibold">Name</th>
                    <th className="px-3 py-3 font-semibold">Email</th>
                    <th className="px-3 py-3 font-semibold">Company</th>
                    <th className="px-3 py-3 font-semibold">Role</th>
                    <th className="whitespace-nowrap px-3 py-3 font-semibold">Volume</th>
                    <th className="px-3 py-3 font-semibold">Doc Types</th>
                    <th className="px-3 py-3 font-semibold">Message</th>
                    <th className="w-10 px-3 py-3 font-semibold">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((l) => (
                    <tr
                      key={l.id}
                      data-testid="admin-lead-row"
                      className="border-b border-slate-100 align-top last:border-0 hover:bg-slate-50"
                    >
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-500">{fmtDate(l.created_at)}</td>
                      <td className="px-3 py-3 font-medium text-slate-900">{l.name}</td>
                      <td className="break-all px-3 py-3">
                        <a href={`mailto:${l.email}`} className="text-brand hover:underline">{l.email}</a>
                      </td>
                      <td className="px-3 py-3 text-slate-700">{l.company}</td>
                      <td className="px-3 py-3 text-slate-500">{l.role || "—"}</td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-slate-500">{l.document_volume || "—"}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(l.document_types || []).length
                            ? l.document_types.map((t) => (
                                <span key={t} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-600">
                                  {t}
                                </span>
                              ))
                            : <span className="text-slate-400">—</span>}
                        </div>
                      </td>
                      <td className="max-w-xs px-3 py-3 text-slate-600">
                        <span className="line-clamp-2" title={l.message || ""}>{l.message || "—"}</span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              data-testid={`lead-actions-${l.id}`}
                              aria-label="Lead actions"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              data-testid={`lead-delete-${l.id}`}
                              onClick={() => setLeadToDelete(l)}
                              className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700"
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loadingLeads && filtered.length > 0 && (
            <div
              data-testid="admin-pagination"
              className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-4 py-4 sm:flex-row sm:px-6"
            >
              <p className="text-xs text-slate-500 sm:text-sm">
                Showing <span className="font-semibold text-slate-700">{rangeStart}–{rangeEnd}</span> of{" "}
                <span className="font-semibold text-slate-700">{filtered.length}</span>
                {query ? " matching" : ""} lead{filtered.length === 1 ? "" : "s"}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="admin-pagination-prev"
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-full"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" /> Prev
                </Button>
                <span data-testid="admin-pagination-info" className="px-2 text-sm font-medium text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  data-testid="admin-pagination-next"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-full"
                >
                  Next <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!leadToDelete} onOpenChange={(open) => !open && setLeadToDelete(null)}>
        <AlertDialogContent data-testid="delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the lead
              {leadToDelete ? ` from ${leadToDelete.name || leadToDelete.email}` : ""}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-lead-cancel" disabled={deleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-lead-confirm"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-500"
            >
              {deleting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting</>
              ) : (
                <><Trash2 className="mr-2 h-4 w-4" /> Delete</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
